/**
 * Capacitor bridge — helpers do aplicativo nativo (Android).
 *
 * Tudo aqui é seguro para rodar no navegador: `@capacitor/core` funciona em
 * qualquer ambiente (`Capacitor.isNativePlatform()` retorna false no web), e o
 * plugin de notificações nativas só é carregado sob demanda (import dinâmico),
 * para não inflar o bundle web nem quebrar o `vite build`.
 */
import { Capacitor } from '@capacitor/core'
import type { AppNotification } from '@/stores/notifications'

// Evita que uma sequência de erros/reconexões do boot vire uma rajada de
// notificações no Redmi. A notificação continua disponível, mas o mesmo alerta
// é agrupado por 60s e há um intervalo mínimo de 5s entre alertas nativos.
const nativeNotificationTimes = new Map<string, number>()
const NATIVE_NOTIFICATION_COOLDOWN_MS = 60_000
const NATIVE_NOTIFICATION_MIN_INTERVAL_MS = 5_000
let lastNativeNotificationAt = 0

/** true quando o app roda dentro do WebView nativo (Android via Capacitor). */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform()
}

/**
 * Solicita a permissão de notificação (Android 13+ exige POST_NOTIFICATIONS em
 * runtime). Deve ser chamada num gesto do usuário (ex.: clique no sino).
 */
export async function requestNativeNotificationPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!isNativePlatform()) return 'prompt'
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications')
    let status = await LocalNotifications.checkPermissions()
    if (status.display !== 'granted') {
      status = await LocalNotifications.requestPermissions()
    }
    return status.display
  } catch {
    return 'prompt'
  }
}

/**
 * Mostra a notificação como notificação NATIVA do Android quando o app está
 * em primeiro plano (mesma regra do navegador: só tons de alerta error/warning,
 * para não bombardear o usuário com sucessos/infos).
 *
 * Limitação honesta: notificações locais só disparam enquanto o app está
 * ativo no dispositivo. Para receber eventos com o app fechado é preciso
 * push real (FCM + @capacitor/push-notifications) — fica para uma próxima fase.
 */
export async function notifyNativeLocal(note: AppNotification): Promise<void> {
  if (!isNativePlatform()) return
  if (note.tone !== 'error' && note.tone !== 'warning') return
  try {
    const now = Date.now()
    const key = `${note.tone}:${note.title}:${note.body ?? ''}`
    const lastForKey = nativeNotificationTimes.get(key) ?? 0
    if (now - lastNativeNotificationAt < NATIVE_NOTIFICATION_MIN_INTERVAL_MS) return
    if (now - lastForKey < NATIVE_NOTIFICATION_COOLDOWN_MS) return

    const { LocalNotifications } = await import('@capacitor/local-notifications')
    const status = await LocalNotifications.checkPermissions()
    if (status.display !== 'granted') return
    // LocalNotifications exige id numérico — deriva um estável do id string.
    const numericId = stableNumericId(note.id)
    await LocalNotifications.schedule({
      notifications: [
        {
          id: numericId,
          title: note.title,
          body: note.body ?? '',
          schedule: { at: new Date(Date.now() + 400) },
          extra: { href: note.href ?? null, notificationId: note.id },
        },
      ],
    })
    nativeNotificationTimes.set(key, now)
    lastNativeNotificationAt = now
  } catch {
    /* ambiente sem plugin — ignora silenciosamente */
  }
}

/** Hash simples de string → int32 estável (id numérico p/ LocalNotifications). */
function stableNumericId(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0
  }
  return Math.abs(hash) || 1
}
